import React, { PureComponent } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";

const baseUrl = process.env.REACT_APP_BASE_URL;

/**
 * Public "Verify Premises" page. Replaces the old Verify License page, which searched member
 * licences by surname and listed every match.
 *
 * Answers one question: does this premises hold a current, active NiCFoST premises licence?
 * Searchable by premises name, company name, or licence number.
 *
 * Where a search matches several distinct licensed premises the backend returns MULTIPLE_MATCHES
 * and no details, and this page asks the searcher to narrow by licence number.
 */
class VerifyPremise extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      query: "",
      isLoading: false,
      result: null,
    };
  }

  verify = async (event) => {
    if (event) event.preventDefault();

    const query = this.state.query.trim();
    if (!query) {
      this.setState({
        result: {
          outcome: "QUERY_REQUIRED",
          message: "Enter a premises name, company name, or licence number to search.",
        },
      });
      return;
    }

    this.setState({ isLoading: true, result: null });

    try {
      // Licence numbers contain slashes, so the term goes in a query parameter. Encoded into a
      // path segment it becomes %2F, which Tomcat rejects with a 400 before Spring ever sees it.
      const response = await fetch(
        `${baseUrl}/api/public/verification/premises?query=${encodeURIComponent(query)}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );

      if (!response.ok) {
        throw new Error(`Verification service returned ${response.status}`);
      }

      this.setState({ result: await response.json(), isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false, result: null });
      Swal.fire({
        title: "Could not complete the check",
        text: `${error.message}. Please try again in a moment.`,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  renderResult() {
    const { result } = this.state;
    if (!result) return null;

    if (result.outcome === "VERIFIED") {
      const { premisesName, companyName, licenseNumber, state, expiryDate } =
        result.premises || {};
      const expiry = expiryDate ? moment(expiryDate).format("LL") : null;

      return (
        <div className="card my-3 border border-success">
          <div className="card-header pb-3 bg-success">
            <h5 className="text-light text-center mb-0">Verified</h5>
          </div>
          <div className="card-body">
            <p className="text-secondary text-center mb-4">{result.message}</p>
            <h4 className="font-weight-bold text-capitalize text-center mb-4">{premisesName}</h4>
            <div className="row">
              <div className="col-6 text-secondary mb-2">Company</div>
              <div className="col-6 text-end font-weight-bold text-capitalize mb-2">
                {companyName}
              </div>
              <div className="col-6 text-secondary mb-2">Licence number</div>
              <div className="col-6 text-end font-weight-bold mb-2">{licenseNumber}</div>
              <div className="col-6 text-secondary mb-2">State</div>
              <div className="col-6 text-end font-weight-bold text-capitalize mb-2">{state}</div>
              {expiry && (
                <>
                  <div className="col-6 text-secondary">Valid until</div>
                  <div className="col-6 text-end font-weight-bold">{expiry}</div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    const isAmbiguous =
      result.outcome === "MULTIPLE_MATCHES" || result.outcome === "QUERY_REQUIRED";

    return (
      <div className={`card my-3 border ${isAmbiguous ? "border-warning" : "border-danger"}`}>
        <div className={`card-header pb-3 ${isAmbiguous ? "bg-warning" : "bg-danger"}`}>
          <h5 className="text-light text-center mb-0">
            {result.outcome === "MULTIPLE_MATCHES"
              ? "Narrow your search"
              : result.outcome === "QUERY_REQUIRED"
              ? "Search required"
              : "Not verified"}
          </h5>
        </div>
        <div className="card-body text-center">
          <p className="mb-0">{result.message}</p>
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, query } = this.state;

    return (
      <div className="container" style={{ display: "flex", flexDirection: "column" }}>
        <center className="mb-4">
          <img
            src="../assets/images/nicfost.png"
            className="navbar-brand-img"
            style={{ width: 90, height: 90, marginTop: 27, marginBottom: 18 }}
            alt="main_logo"
          />
          <h2 className="text-uppercase">Verify Premises</h2>
          <a href="https://nicfost.gov.ng">Go Back Home</a>
        </center>

        <p className="mt-4 font-weight-bold text-center">
          Enter premises name, company name, or licence number
        </p>

        <center>
          <form
            onSubmit={this.verify}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              width: "50%",
            }}
          >
            <div className="input-group input-group-outline mb-3">
              <input
                type="text"
                className="form-control shadow-none col-lg-8"
                value={query}
                onChange={(e) => this.setState({ query: e.target.value })}
              />
            </div>
            <button
              type="submit"
              style={{ backgroundColor: "#003314" }}
              className="btn btn-success col-lg-4 btn-lg"
              disabled={isLoading}
            >
              {isLoading ? <Spinner animation="border" variant="light" size="sm" /> : "Search"}
            </button>
          </form>
        </center>

        <div className="container" style={{ maxWidth: 640 }}>
          {this.renderResult()}
        </div>
      </div>
    );
  }
}

export default VerifyPremise;
